import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Input, DatePicker, Button, Table, Divider, Space, message, Form, InputNumber, Typography, Modal, Tooltip } from 'antd';
import { ToolOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import useAuthStore from '../../store/useAuthStore';
import usePermissions from '../../hooks/usePermissions';
import useDebounce from '../../hooks/useDebounce';
import { MODULE_IDS } from '../../utils/constants';
import { formatQty, formatDate } from '../../utils/formatters';

import { getProductions, getProductionById, createProduction, deleteProduction, getConsumptionPreview } from '../../api/production.api';
import { getAllRawMaterials } from '../../api/rawMaterials.api';

import PageHeader from '../../components/common/PageHeader';
import SearchableSelect from '../../components/common/SearchableSelect';
import ConfirmDelete from '../../components/common/ConfirmDelete';
import DataTable from '../../components/common/DataTable';
import StockBadge from '../../components/common/StockBadge';

const { Text } = Typography;


const ExpandedProductionDetails = ({ plantId, pid }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['productionDetail', plantId, pid],
    queryFn: () => getProductionById(plantId, pid),
    enabled: !!pid && !!plantId,
  });

  const columns = [
    { title: 'Raw Material', dataIndex: 'rawName', key: 'rawName' },
    { title: 'Expected Qty', dataIndex: 'expectedQty', key: 'expectedQty', render: val => formatQty(val) },
    { title: 'Actual Qty', dataIndex: 'consumptionQty', key: 'consumptionQty', render: val => <b>{formatQty(val)}</b> },
    { title: 'Wastage', dataIndex: 'wastageQty', key: 'wastageQty', render: val => formatQty(val) },
    { title: 'Unit', dataIndex: 'unitDisplayName', key: 'unitDisplayName' },
  ];

  return <Table columns={columns} dataSource={data?.consumptionDetails || []} pagination={false} size="small" loading={isLoading} rowKey="rcid" />;
};


const ProductionEntryPage = () => {
  const { user } = useAuthStore();
  const plantId = user?.plantID;
  const queryClient = useQueryClient();
  const { canAdd, canDelete } = usePermissions(MODULE_IDS.PRODUCTION);

  const [form] = Form.useForm();
  const [showEntryForm, setShowEntryForm] = useState(false);


  const [previewItems, setPreviewItems] = useState([]);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [historyParams, setHistoryParams] = useState({ current: 1, pageSize: 10, from: null, to: null, readyProductId: null });


  // Always use readyProductId for useWatch and setFieldsValue
  const selectedProduct = Form.useWatch('readyProductId', form);
  const productionQty = Form.useWatch('productionQty', form);
  const debouncedQty = useDebounce(productionQty, 500);

  // Force form to update and re-register fields when entry form is shown
  useEffect(() => {
    if (showEntryForm) {
      form.resetFields();
      setPreviewItems([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEntryForm]);

  
  const { data: rawMaterialsData } = useQuery({
    queryKey: ['rawMaterialsStock', plantId],
    queryFn: () => getAllRawMaterials(plantId, { pageSize: 1000 }),
    enabled: !!plantId,
  });

  
  const stockMap = useMemo(() => {
    const map = {};
    if (rawMaterialsData?.items) {
      rawMaterialsData.items.forEach(rm => { map[rm.rawID] = rm.balanceQty; });
    }
    return map;
  }, [rawMaterialsData]);


  useEffect(() => {
    const fetchPreview = async () => {
      if (selectedProduct && debouncedQty > 0) {
        setIsPreviewLoading(true);
        try {
          const previewData = await getConsumptionPreview(plantId, selectedProduct, debouncedQty);
          // Map preview data to local state for editing
          const editableItems = previewData.map(item => {
            // Get mFactor from raw materials data
            const rawMaterial = rawMaterialsData?.items?.find(rm => rm.rawID === item.rawMaterialID);
            return {
              ...item,
              actualQty: item.expectedQty, // default to expected
              wastageQty: item.wastageQty || 0,
              mFactor: rawMaterial?.mFactor || item.mFactor || 1 // Add mFactor
            };
          });
          setPreviewItems(editableItems);
        } catch (error) {
          setPreviewItems([]);
          if (error.message.includes('No recipe')) {
             message.warning("No recipe (mapping) found for this product. Consumption won't auto-calculate.");
          }
        } finally {
          setIsPreviewLoading(false);
        }
      } else {
        setPreviewItems([]);
      }
    };
    fetchPreview();
  }, [selectedProduct, debouncedQty, plantId, rawMaterialsData]);

  // --- React Query: Fetch History ---
  const { data: historyData, isLoading: isHistoryLoading } = useQuery({
    queryKey: ['productions', plantId, historyParams],
    queryFn: () => getProductions(plantId, {
      page: historyParams.current,
      pageSize: historyParams.pageSize,
      from: historyParams.from ? historyParams.from.format('YYYY-MM-DD') : null,
      to: historyParams.to ? historyParams.to.format('YYYY-MM-DD') : null,
      readyProductId: historyParams.readyProductId
    }),
    enabled: !!plantId,
  });

  // --- Mutations ---
  const createMutation = useMutation({
    mutationFn: (payload) => createProduction(plantId, payload),
    onSuccess: () => {
      message.success('Production entry saved and stock updated successfully!');
      form.resetFields();
      setPreviewItems([]);
      queryClient.invalidateQueries(['productions']);
      queryClient.invalidateQueries(['rawMaterialsStock']); // Refresh stock map
    },
    onError: (err) => message.error(err.message || 'Error saving production')
  });

  const deleteMutation = useMutation({
    mutationFn: (pid) => deleteProduction(plantId, pid),
    onSuccess: () => {
      message.success('Production deleted and stock reversed successfully!');
      queryClient.invalidateQueries(['productions']);
      queryClient.invalidateQueries(['rawMaterialsStock']);
    },
    onError: (err) => message.error(err.message || 'Error deleting production')
  });

  // --- Handlers ---
  const handleUpdateItem = (rawId, field, value) => {
    const updatedItems = previewItems.map(item => {
      if (item.rawMaterialID === rawId) {
        return { ...item, [field]: value };
      }
      return item;
    });
    setPreviewItems(updatedItems);
  };

  const handleSaveProduction = async () => {
    try {
      const values = await form.validateFields();

      // Check if any row has Actual Qty > Current Stock (in consumption units)
      const hasInsufficientStock = previewItems.some(item => {
        const purchaseStock = stockMap[item.rawMaterialID] || 0;
        const consumptionStock = purchaseStock * (item.mFactor || 1);
        return item.actualQty > consumptionStock;
      });

      const payload = {
        readyProductID: values.readyProductId,
        productionQty: values.productionQty,
        productionDate: values.productionDate.format('YYYY-MM-DD'),
        batchNo: values.batchNo,
        remarks: values.remarks,
        consumptionDetails: previewItems.map(item => ({
          rawMaterialID: item.rawMaterialID,
          consumptionQty: item.actualQty,
          wastageQty: item.wastageQty,
          unitID: item.unitID
        }))
      };

      if (hasInsufficientStock) {
        Modal.confirm({
          title: 'Insufficient Stock Warning',
          icon: <ExclamationCircleOutlined style={{ color: 'orange' }}/>,
          content: 'One or more raw materials have insufficient stock for this production. Do you want to proceed and allow negative stock?',
          okText: 'Yes, Proceed',
          cancelText: 'Cancel',
          onOk: () => createMutation.mutate(payload)
        });
      } else {
        createMutation.mutate(payload);
      }
    } catch (error) {
      console.log('Validation Failed:', error);
    }  
  };

  // --- Table Configurations ---
  const previewColumns = [
    { title: '#', render: (text, record, index) => index + 1, width: 50 },
    { title: 'Raw Material', dataIndex: 'rawName', key: 'rawName' },
    { title: 'Unit', dataIndex: 'unitDisplayName', key: 'unitDisplayName' },
    { title: 'Expected Qty', dataIndex: 'expectedQty', key: 'expectedQty', render: val => <Text type="secondary">{formatQty(val)}</Text> },
    { 
      title: 'Actual Qty', 
      key: 'actualQty',
      render: (_, record) => (
        <InputNumber 
          min={0} 
          step="any" 
          value={record.actualQty} 
          onChange={(val) => handleUpdateItem(record.rawMaterialID, 'actualQty', val || 0)} 
        />
      )
    },
    { 
      title: 'Wastage Qty', 
      key: 'wastageQty',
      render: (_, record) => (
        <InputNumber 
          min={0} 
          step="any" 
          value={record.wastageQty} 
          onChange={(val) => handleUpdateItem(record.rawMaterialID, 'wastageQty', val || 0)} 
        />
      )
    },
    { 
      title: 'Current Stock', 
      key: 'currentStock', 
      render: (_, record) => {
        console.log('mFactor for', record.rawName, ':', record.mFactor);
        const purchaseStock = stockMap[record.rawMaterialID] || 0;
        // Convert purchase stock to consumption stock using mFactor
        const consumptionStock = purchaseStock * (record.mFactor || 1);
        const isLow = record.actualQty > consumptionStock;
        return (
          <Space>
            <Text type={isLow ? 'danger' : 'secondary'} strong={isLow}>{formatQty(consumptionStock)}</Text>
            <StockBadge qty={consumptionStock} threshold={10} />
            {isLow && <Tooltip title="Insufficient Stock"><ExclamationCircleOutlined style={{ color: 'red' }} /></Tooltip>}
          </Space>
        );
      }
    }
  ];

  const historyColumns = [
    { title: 'Date', dataIndex: 'productionDate', key: 'productionDate', render: val => formatDate(val) },
    { title: 'Batch No', dataIndex: 'batchNo', key: 'batchNo', render: val => val || '-' },
    { title: 'Product', dataIndex: 'productName', key: 'productName' },
    { title: 'Qty Produced', dataIndex: 'productionQty', key: 'productionQty', render: val => <b>{formatQty(val)}</b> },
    { title: 'Entry By', dataIndex: 'entryBy', key: 'entryBy' },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {canDelete && (
            <ConfirmDelete 
              itemName="Production & Reverse Stock" 
              onConfirm={() => deleteMutation.mutate(record.pid)} 
            />
          )}
        </Space>
      )
    }
  ];

  // Inline styling for rows that exceed stock (in consumption units)
  const rowClassName = (record) => {
    const purchaseStock = stockMap[record.rawMaterialID] || 0;
    const consumptionStock = purchaseStock * (record.mFactor || 1);
    return record.actualQty > consumptionStock ? 'error-row-highlight' : '';
  };

  return (
    <div>
      {/* Quick inline style for error highlight */}
      <style>{`
        .error-row-highlight { background-color: #fff2f0 !important; }
      `}</style>

      <PageHeader 
        title="Production Entry" 
        subtitle="Record manufactured goods and auto-consume raw materials" 
        canAdd={canAdd} 
        addLabel="Add New Production Entry"
        onAdd={() => {
          setShowEntryForm(true);
          form.resetFields();
          setPreviewItems([]);
        }}
      />

      {/* ENTRY FORM and PREVIEW only if canAdd and showEntryForm */}
      {canAdd && showEntryForm && (
        <>
          <Card title="New Production Entry" style={{ marginBottom: 16 }}>
            <Form form={form} layout="vertical" initialValues={{ productionDate: dayjs() }}>
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item label="Ready Product" name="readyProductId" rules={[{ required: true, message: 'Required' }]}> 
                    <SearchableSelect
                      apiUrl={`/plants/${plantId}/ready-products`}
                      valueField="rpid"
                      labelField="productName"
                      placeholder="Select product to manufacture"
                      onSelect={val => {
                        form.setFieldsValue({ readyProductId: val?.rpid });
                        setPreviewItems([]);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} md={8}>
                  <Form.Item label="Production Qty" name="productionQty" rules={[{ required: true, message: 'Required' }]}>
                    <InputNumber min={0.0001} style={{ width: '100%' }} placeholder="How many units?" />
                  </Form.Item>
                </Col>
                <Col xs={12} md={8}>
                  <Form.Item label="Production Date" name="productionDate" rules={[{ required: true, message: 'Required' }]}>
                    <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16} align="bottom">
                <Col xs={12} md={8}>
                  <Form.Item label="Batch No." name="batchNo">
                    <Input placeholder="e.g. BATCH-2025-001" />
                  </Form.Item>
                </Col>
                <Col xs={16} md={12}>
                  <Form.Item label="Remarks" name="remarks">
                    <Input placeholder="Any optional notes" />
                  </Form.Item>
                </Col>
                <Col xs={8} md={4} style={{ textAlign: 'right' }}>
                  <Button 
                    type="primary" 
                    size="large" 
                    icon={<ToolOutlined />} 
                    onClick={handleSaveProduction}
                    loading={createMutation.isPending}
                    disabled={previewItems.length === 0}
                    style={{ width: '100%' }}
                  >
                    Save Production
                  </Button>
                  {previewItems.length === 0 && (
                    <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>
                      Please select a Ready Product and enter Production Qty to load raw materials.
                    </div>
                  )}
                </Col>
              </Row>
            </Form>
          </Card>

          {/* CONSUMPTION PREVIEW TABLE */}
          {selectedProduct && debouncedQty > 0 && (
            <Card title="Raw Material Consumption (Auto-calculated from recipe)" style={{ marginBottom: 16 }}>
              <Table 
                columns={previewColumns} 
                dataSource={previewItems} 
                pagination={false} 
                rowKey="rawMaterialID"
                loading={isPreviewLoading}
                rowClassName={rowClassName}
                locale={{ emptyText: "No recipe mapping found for this product. Please define one in Masters." }}
                scroll={{ x: 'max-content' }}
              />
            </Card>
          )}
        </>
      )}

      <Divider style={{ margin: '32px 0' }} orientation="left">Previous Productions</Divider>

      {/* PREVIOUS PRODUCTIONS */}
      <div style={{ marginBottom: 16 }}>
         <Row gutter={16}>
           <Col xs={24} md={8}>
              <DatePicker.RangePicker 
                style={{ width: '100%' }}
                onChange={(dates) => {
                  setHistoryParams(prev => ({ 
                    ...prev, 
                    current: 1, 
                    from: dates ? dates[0] : null, 
                    to: dates ? dates[1] : null 
                  }));
                }} 
              />
           </Col>
           <Col xs={24} md={8}>
             <SearchableSelect
                apiUrl={`/plants/${plantId}/ready-products`}
                valueField="rpid"
                labelField="productName"
                placeholder="Filter by Product"
                onSelect={(v) => {
                  setHistoryParams(prev => ({ ...prev, current: 1, readyProductId: v ? v.rpid : null }));
                }}
              />
           </Col>
         </Row>
      </div>

      <DataTable 
        columns={historyColumns} 
        data={historyData?.items || []} 
        loading={isHistoryLoading} 
        rowKey="pid"
        pagination={{
          current: historyParams.current,
          pageSize: historyParams.pageSize,
          total: historyData?.total || 0
        }}
        onChange={(pagination) => setHistoryParams(prev => ({ ...prev, current: pagination.current, pageSize: pagination.pageSize }))}
        expandable={{
          expandedRowRender: (record) => <ExpandedProductionDetails plantId={plantId} pid={record.pid} />,
        }}
      />
    </div>
  );
};

export default ProductionEntryPage;